import React, { useEffect, useState } from 'react';
import { useNotification } from '../context/NotificationContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { toast } from 'react-toastify';

const NotificationPrompt: React.FC = () => {
  const { isPermissionGranted, requestPermission, isLoading } = useNotification();
  const { isAuthenticated } = useAuth();
  const [hasPrompted, setHasPrompted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Auto-prompt after login if permission not granted
  useEffect(() => {
    if (isAuthenticated && !isPermissionGranted && !hasPrompted) {
      // Wait a bit after login, then show banner
      const timer = setTimeout(() => {
        setShowBanner(true);
        setHasPrompted(true);
      }, 2000); // 2 seconds after login

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isPermissionGranted, hasPrompted]);

  // Hide banner if permission is granted
  useEffect(() => {
    if (isPermissionGranted) {
      setShowBanner(false);
    }
  }, [isPermissionGranted]);

  // Auto-request permission when banner shows
  const handleEnableClick = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success('Notifications enabled successfully!');
      setShowBanner(false);
    } else {
      toast.error('Please enable notifications in your device settings');
    }
  };

  // Don't show if permission already granted or not authenticated
  if (!isAuthenticated || isPermissionGranted || !showBanner) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-primary text-white shadow-lg animate-slideDown">
      <div className="container mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center flex-1 min-w-0">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-sm font-medium truncate">Enable notifications to stay updated</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowBanner(false)}
              className="text-white/80 hover:text-white p-1"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              onClick={handleEnableClick}
              disabled={isLoading}
              className="bg-white text-primary px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {isLoading ? 'Enabling...' : 'Enable'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;

